import React, { useState, useEffect, useCallback } from 'react';
import { Toaster } from './components/ui/sonner';
import LandingPage from './components/LandingPage';
import CalculatorsPage from './components/CalculatorsPage';
import WalkingSetup from './components/WalkingSetup';
import WalkingTimer from './components/WalkingTimer';
import WalkingHistory from './components/WalkingHistory';
import WalkingStatistics from './components/WalkingStatistics';
import MainApp from './components/MainApp';
import BackgroundTheme from './components/BackgroundTheme';
import AdminPanel from './components/AdminPanel';
import AuthModal from './components/AuthModal';
import TwoFactorModal from './components/personal/TwoFactorModal';
import { TwoFactorInline } from './components/personal/TwoFactorModal';
import { ThemeProvider } from './components/ThemeProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SyncProvider } from './contexts/SyncContext';
import useCloudSync from './hooks/useCloudSync';
import { Volume2, VolumeX, History, Home, Palette, ArrowLeft, CalendarDays, Footprints, BarChart3 } from 'lucide-react';
import { Button } from './components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { BottomNavBar } from './components/navigation';
import api from './services/api';
import { trackPageView } from './services/analyticsService';
import { toast } from 'sonner';
import { migrateStorageKeys, STORAGE_KEYS } from './utils/storageKeys';
import './App.css';

// Run storage key migration on app load (before React renders)
migrateStorageKeys();

const API_URL = '';

function AppContent() {
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  
  // Check for admin route on mount using lazy initializer
  const [currentApp, setCurrentApp] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('admin') === 'true') {
      return 'admin';
    }
    return 'landing';
  }); // 'landing', 'walking', 'notebook', 'admin', 'calculators'
  const [screen, setScreen] = useState('setup');
  const [notebookInitialView, setNotebookInitialView] = useState(null);
  const [walkingConfig, setWalkingConfig] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const { refreshUser, isAuthenticated, loading: authLoading, pending2FA, setPending2FA, complete2FALogin } = useAuth();
  
  // Cloud sync hook — auto-syncs localStorage to/from MongoDB
  const { schedulePush } = useCloudSync(isAuthenticated);
  
  // App Version - for global update checking
  const APP_VERSION = '1.0';
  
  // Detect iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  // Watch for authentication state changes to navigate after login
  useEffect(() => {
    if (isAuthenticated && showAuthModal && currentApp === 'landing') {
      // User just logged in from landing page, close modal and go to app immediately
      setShowAuthModal(false);
      setCurrentApp('notebook');
    }
  }, [isAuthenticated, showAuthModal, currentApp]);
  
  // Auto-redirect authenticated users from landing to app
  useEffect(() => {
    if (!authLoading && isAuthenticated && currentApp === 'landing') {
      setCurrentApp('notebook');
    }
  }, [authLoading, isAuthenticated, currentApp]);
  
  // Browser history support for app navigation
  const setCurrentAppWithHistory = useCallback((app, screen = null) => {
    if (app !== 'landing') {
      const state = { app, screen };
      window.history.pushState(state, '', `#${app}${screen ? `-${screen}` : ''}`);
    }
    setCurrentApp(app);
    if (screen) setScreen(screen);
  }, []);
  
  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (event) => {
      const state = event.state;
      if (state?.app) {
        setCurrentApp(state.app);
        if (state.screen) setScreen(state.screen);
      } else {
        // No state means we're at the beginning - go to notebook (main app) or landing
        if (isAuthenticated) {
          setCurrentApp('notebook');
        } else {
          setCurrentApp('landing');
        }
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated]);
  
  // Track page views for analytics
  useEffect(() => {
    // Map currentApp to page names for analytics
    const pageMap = {
      'landing': 'landing',
      'calculators': 'calculators',
      'notebook': 'app',
      'walking': 'walking',
      'admin': 'admin'
    };
    const pageName = pageMap[currentApp] || currentApp;
    
    // Store current page for visibility change tracking
    window.currentAnalyticsPage = pageName;
    
    // Track the page view
    trackPageView(pageName);
  }, [currentApp]);
  
  // Version check disabled to prevent refresh loops
  // Updates are now handled manually by the user refreshing the page
  
  // Handle Stripe payment redirect
  useEffect(() => {
    // Wait for auth to finish loading before checking payment
    if (authLoading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    // Only process Stripe sessions (start with cs_)
    if (sessionId && sessionId.startsWith('cs_')) {
      // Check payment status only if authenticated
      const checkPayment = async () => {
        if (isAuthenticated) {
          try {
            const status = await api.getCheckoutStatus(sessionId);
            if (status.payment_status === 'paid') {
              toast.success('🎉 Payment successful! Subscription activated.');
              await refreshUser();
            }
          } catch (err) {
            console.error('Payment check failed:', err);
            // If auth failed, don't show error - user will see login screen
          }
        }
        // Clean up URL regardless of auth status
        window.history.replaceState({}, document.title, window.location.pathname);
      };
      checkPayment();
    }
  }, [isAuthenticated, authLoading, refreshUser]);
  
  // AGGRESSIVE CLEANUP: Remove all old training data on app start
  useEffect(() => {
    const cleanupOldData = () => {
      const cutoffDate = new Date('2025-01-13T00:00:00Z').getTime();
      
      // List of all possible storage keys for training data
      const keysToClean = [
        'gym_notebook_training_history',
        'fitpro_training_history',
        'training_history',
        'gym_notebook_active_plan',
        'gym_notebook_exercise_history',
        'gym_notebook_favorites'
      ];
      
      let totalRemoved = 0;
      
      keysToClean.forEach(key => {
        const saved = localStorage.getItem(key);
        if (saved) {
          try {
            const data = JSON.parse(saved);
            if (Array.isArray(data)) {
              const cleaned = data.filter(entry => {
                const dateStr = entry.date || entry.completedAt || '';
                if (!dateStr) return true; // Keep entries without dates
                
                // Specifically remove 11.01 and 12.01 entries (both 2025 and 2026)
                if (dateStr.includes('2025-01-11') || dateStr.includes('2025-01-12') ||
                    dateStr.includes('2026-01-11') || dateStr.includes('2026-01-12')) {
                  console.log(`[Cleanup] Removing old entry: ${dateStr}`);
                  return false;
                }
                
                const entryDate = new Date(dateStr).getTime();
                return !isNaN(entryDate) && entryDate >= cutoffDate;
              });
              
              if (cleaned.length !== data.length) {
                const removed = data.length - cleaned.length;
                totalRemoved += removed;
                console.log(`[Cleanup] Removed ${removed} old entries from ${key}`);
                localStorage.setItem(key, JSON.stringify(cleaned));
              }
            }
          } catch (e) {
            console.log(`[Cleanup] Clearing corrupted data from ${key}`);
            localStorage.removeItem(key);
          }
        }
      });
      
      // Also clean exercise history object (not array)
      const exerciseHistoryKey = 'gym_notebook_exercise_history';
      const exerciseHistory = localStorage.getItem(exerciseHistoryKey);
      if (exerciseHistory) {
        try {
          const data = JSON.parse(exerciseHistory);
          if (typeof data === 'object' && !Array.isArray(data)) {
            const cleaned = {};
            Object.keys(data).forEach(exerciseName => {
              const entries = data[exerciseName];
              if (Array.isArray(entries)) {
                cleaned[exerciseName] = entries.filter(entry => {
                  const dateStr = entry.date || '';
                  if (dateStr.includes('2025-01-11') || dateStr.includes('2025-01-12') ||
                      dateStr.includes('2026-01-11') || dateStr.includes('2026-01-12')) {
                    return false;
                  }
                  const entryDate = new Date(dateStr).getTime();
                  return !isNaN(entryDate) && entryDate >= cutoffDate;
                });
              }
            });
            localStorage.setItem(exerciseHistoryKey, JSON.stringify(cleaned));
          }
        } catch (e) {
          // Ignore
        }
      }
      
      if (totalRemoved > 0) {
        console.log(`[Cleanup] Total removed: ${totalRemoved} old entries`);
      }
    };
    
    cleanupOldData();
  }, []);
  
  // Use lazy initialization for localStorage values
  const [walkingHistory, setWalkingHistory] = useState(() => {
    const saved = localStorage.getItem('gym_notebook_walking_session_history');
    return saved ? JSON.parse(saved) : [];
  });
  const [bgTheme, setBgTheme] = useState(() => {
    return localStorage.getItem('gym_notebook_bg_theme') || 'steel-gray';
  });

  // Load walking data from backend on mount when authenticated
  useEffect(() => {
    const loadWalkingDataFromBackend = async () => {
      const token = localStorage.getItem('gym_notebook_auth_token');
      if (!token) return;
      
      try {
        // Load walking sessions
        const sessionsResponse = await fetch(`${API_URL}/api/v2/data/walking-sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          if (sessionsData.sessions && sessionsData.sessions.length > 0) {
            // Merge with localStorage (backend takes priority)
            const localSessions = JSON.parse(localStorage.getItem('gym_notebook_walking_session_history') || '[]');
            const mergedSessions = [...sessionsData.sessions];
            // Add local sessions that aren't in backend (by id)
            const backendIds = new Set(sessionsData.sessions.map(s => s.id));
            localSessions.forEach(s => {
              if (!backendIds.has(s.id)) {
                mergedSessions.push(s);
              }
            });
            localStorage.setItem('gym_notebook_walking_session_history', JSON.stringify(mergedSessions));
            console.log('Walking sessions loaded from backend:', mergedSessions.length);
          }
        }
        
        // Load walking daily data
        const dailyResponse = await fetch(`${API_URL}/api/v2/data/walking-daily`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (dailyResponse.ok) {
          const dailyData = await dailyResponse.json();
          if (dailyData.daily_data && Object.keys(dailyData.daily_data).length > 0) {
            // Merge with localStorage
            const localDaily = JSON.parse(localStorage.getItem('gym_notebook_walking_daily_data') || '{}');
            const mergedDaily = { ...localDaily, ...dailyData.daily_data };
            localStorage.setItem('gym_notebook_walking_daily_data', JSON.stringify(mergedDaily));
            console.log('Walking daily data loaded from backend');
          }
        }
      } catch (error) {
        console.error('Error loading walking data from backend:', error);
      }
    };
    
    if (isAuthenticated && !authLoading) {
      loadWalkingDataFromBackend();
    }
  }, [isAuthenticated, authLoading]);

  const handleThemeChange = (themeId) => {
    setBgTheme(themeId);
    localStorage.setItem('gym_notebook_bg_theme', themeId);
  };

  const getBackgroundClass = () => {
    const themes = {
      // Modern & Minimal
      'midnight-slate': 'bg-[hsl(220,20%,10%)]',
      'pure-dark': 'bg-[hsl(0,0%,8%)]',
      // Bold & Energetic
      'fire-red': 'bg-[hsl(0,60%,12%)]',
      'electric-blue': 'bg-[hsl(210,70%,12%)]',
      'neon-green': 'bg-[hsl(150,60%,10%)]'
    };
    return themes[bgTheme] || themes['midnight-slate'];
  };

  const handleSelectApp = (app) => {
    setCurrentApp(app);
    setScreen('setup');
  };

  const handleBackToLanding = () => {
    setCurrentApp('landing');
    setScreen('setup');
    setWalkingConfig(null);
  };

  const handleStartWalkingWorkout = (config) => {
    setWalkingConfig(config);
    setScreen('walking-timer');
  };

  const handleWalkingComplete = async (summary) => {
    console.log('[Walking] handleWalkingComplete called with:', summary);
    
    const walkingSummary = {
      id: Date.now(),
      date: new Date().toISOString(),
      type: 'walking',
      duration: summary.totalTime,
      quickSets: summary.quickSets,
      slowSets: summary.slowSets,
      steps: summary.steps || 0,
      totalDailySteps: summary.totalDailySteps || 0,
      calories: summary.calories || 0,
      bodyWeight: summary.bodyWeight || 70,
      completed: !summary.incomplete
    };
    
    try {
      const newHistory = [walkingSummary, ...walkingHistory].slice(0, 20);
      setWalkingHistory(newHistory);
      localStorage.setItem('gym_notebook_walking_session_history', JSON.stringify(newHistory));
      console.log('[Walking] Saved to walking_session_history');
      
      const todayDate = new Date().toISOString().split('T')[0];
      const walkingDaily = JSON.parse(localStorage.getItem('gym_notebook_walking_daily_data') || '{}');
      const existingDaily = walkingDaily[todayDate] || { steps: 0, distance: 0, calories: 0 };
      walkingDaily[todayDate] = {
        steps: (existingDaily.steps || 0) + (summary.steps || 0),
        distance: (existingDaily.distance || 0) + (summary.distance || 0),
        calories: (existingDaily.calories || 0) + (summary.calories || 0)
      };
      localStorage.setItem('gym_notebook_walking_daily_data', JSON.stringify(walkingDaily));
      
      console.log('[Walking] Saved to walking_daily_data:', walkingDaily[todayDate]);
      
      if (summary.steps > 0 || summary.calories > 0) {
        toast.success(`Saved: ${summary.steps || 0} steps, ${summary.calories || 0} kcal`);
      }
      
      const token = localStorage.getItem('gym_notebook_auth_token');
      if (token) {
        fetch(`${API_URL}/api/v2/data/walking-sessions`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ sessions: newHistory })
        }).then(() => {
          console.log('[Walking] Sessions synced to backend');
        }).catch(err => {
          console.warn('[Walking] Backend sync failed (will retry later):', err.message);
        });
        
        fetch(`${API_URL}/api/v2/data/walking-daily/${todayDate}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(walkingDaily[todayDate])
        }).then(() => {
          console.log('[Walking] Daily data synced to backend');
        }).catch(err => {
          console.warn('[Walking] Backend daily sync failed:', err.message);
        });
      }
    } catch (error) {
      console.error('[Walking] Error saving walking data:', error);
      toast.error('Failed to save walking data');
    }
    
    handleBackToLanding();
  };

  const handleGoHome = () => {
    setScreen('setup');
  };

  // Show loading screen while checking auth to prevent flicker
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/30 animate-pulse">
            <Footprints className="w-8 h-8 text-white" />
          </div>
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Admin Panel - accessible via ?admin=true
  if (currentApp === 'admin') {
    return (
      <>
        <AdminPanel />
        <Toaster />
      </>
    );
  }

  // Handle successful login from landing page
  const handleLoginSuccess = () => {
    setShowAuthModal(false);
    setCurrentApp('notebook');
  };

  // Landing Page
  if (currentApp === 'landing') {
    return (
      <>
        <LandingPage 
          onGetStarted={() => {
            setAuthModalTab('register');
            setShowAuthModal(true);
          }} 
          onLogin={() => {
            setAuthModalTab('login');
            setShowAuthModal(true);
          }}
          onCalculators={() => setCurrentApp('calculators')}
        />
        <AuthModal 
          open={showAuthModal} 
          onOpenChange={(open) => {
            setShowAuthModal(open);
            if (!open && isAuthenticated) {
              setCurrentApp('notebook');
            }
          }}
          defaultTab={authModalTab}
        />
        {/* Google OAuth 2FA - Full screen overlay */}
        {pending2FA && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/95 backdrop-blur-sm" data-testid="google-2fa-overlay">
            <div className="w-full max-w-md mx-4 bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
              <TwoFactorInline
                pendingToken={pending2FA.pendingToken}
                emailHint={pending2FA.emailHint}
                suspicious={pending2FA.suspicious}
                suspiciousReasons={pending2FA.suspiciousReasons}
                onVerified={(data) => {
                  setPending2FA(null);
                  complete2FALogin(data);
                }}
                onCancel={() => setPending2FA(null)}
              />
            </div>
          </div>
        )}
        <Toaster />
      </>
    );
  }

  // Calculators Page (SEO standalone page)
  if (currentApp === 'calculators') {
    return (
      <>
        <CalculatorsPage 
          onBack={() => setCurrentApp('landing')}
        />
        <Toaster />
      </>
    );
  }

  // Main App (Walking & Nutrition Notebook)
  if (currentApp === 'notebook') {
    const handleGoToWalking = (config) => {
      if (config) {
        setWalkingConfig(config);
        setCurrentAppWithHistory('walking', 'walking-timer');
      } else {
        setCurrentAppWithHistory('walking', 'setup');
      }
    };
    
    return (
      <>
        <MainApp 
          onBack={handleBackToLanding} 
          onGoToWalking={handleGoToWalking}
          initialView={notebookInitialView}
          onViewShown={() => setNotebookInitialView(null)}
        />
        <Toaster />
      </>
    );
  }

  // Walking Timer App
  if (currentApp === 'walking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white pb-20">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/90">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Footprints className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-heading font-bold tracking-tight text-white">Walking Timer</h1>
            </div>
            <div className="flex items-center gap-2">
              {screen === 'setup' && (
                <>
                  {/* Saved Walking Routines Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setScreen('walking-history')}
                    className="rounded-full hover:bg-slate-800"
                    title="Saved Walking Routines"
                    data-testid="saved-routines-btn"
                  >
                    <History className="h-5 w-5 text-amber-400" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6">
          {screen === 'setup' && (
            <WalkingSetup 
              onStartWorkout={handleStartWalkingWorkout}
              onBack={() => setCurrentApp('notebook')}
            />
          )}
          {screen === 'walking-history' && (
            <WalkingHistory
              history={walkingHistory.filter(h => h.type === 'walking')}
              onBack={() => setScreen('setup')}
              onLoadRoutine={(routine) => {
                setWalkingConfig(routine);
                setScreen('walking-timer');
              }}
            />
          )}
          {screen === 'walking-stats' && (
            <WalkingStatistics
              onBack={() => setScreen('setup')}
            />
          )}
          {screen === 'walking-timer' && (
            <WalkingTimer
              config={walkingConfig}
              onComplete={handleWalkingComplete}
              onBack={handleBackToLanding}
            />
          )}
        </main>

        {/* Bottom Navigation Bar */}
        <BottomNavBar 
          currentPage="walking"
          onNavigate={(page) => {
            if (page === null || page === 'home') {
              setCurrentApp('notebook');
            }
          }}
        />

        <Toaster />
      </div>
    );
  }

  // Default: redirect to landing
  return (
    <>
      <LandingPage 
        onGetStarted={() => {
          setAuthModalTab('register');
          setShowAuthModal(true);
        }} 
        onLogin={() => {
          setAuthModalTab('login');
          setShowAuthModal(true);
        }}
        onCalculators={() => setCurrentApp('calculators')}
      />
      <AuthModal 
        open={showAuthModal} 
        onOpenChange={(open) => {
          setShowAuthModal(open);
          if (!open && isAuthenticated) {
            setTimeout(() => setCurrentApp('notebook'), 0);
          }
        }}
        defaultTab={authModalTab}
      />
      {/* Google OAuth 2FA - Full screen overlay */}
      {pending2FA && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/95 backdrop-blur-sm" data-testid="google-2fa-overlay">
          <div className="w-full max-w-md mx-4 bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
            <TwoFactorInline
              pendingToken={pending2FA.pendingToken}
              emailHint={pending2FA.emailHint}
              suspicious={pending2FA.suspicious}
              suspiciousReasons={pending2FA.suspiciousReasons}
              onVerified={(data) => {
                setPending2FA(null);
                complete2FALogin(data);
              }}
              onCancel={() => setPending2FA(null)}
            />
          </div>
        </div>
      )}
      <Toaster />
    </>
  );
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-slate-400 mb-4">Please try refreshing the page</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SyncProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </SyncProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
